(() => {
  const normalize = value => String(value || '').trim().toLowerCase();
  const general = /^(all|frontpage|new-arrivals|featured-finds)$|^homepage-/;
  const categories = item => (item.collections || []).map(normalize).filter(value => !general.test(value));
  const overlap = (a, b) => a.filter(value => b.includes(value)).length;
  const tokens = title => [...new Set(normalize(title).split(/[^\p{L}\p{N}]+/u).filter(word => word.length > 3 && !['with','from','single','bundle','pack','company'].includes(word)))];
  const engagement = item => Math.min(1.8, .5 + Math.min(Number(item.views) || 0, 5) * .15 + Math.min(Number(item.seconds) || 0, 180) / 150);
  function score(item, current, history) {
    const type = normalize(item.type);
    const vendor = normalize(item.vendor);
    const shared = overlap(categories(item), categories(current));
    const sameType = type && type === normalize(current.type);
    let value = (sameType ? 90 : 0) + Math.min(shared, 2) * 45;
    if (vendor && vendor === normalize(current.vendor)) value += 18;
    value += Math.min(overlap(tokens(item.title), tokens(current.title)), 5) * 5;
    value += Math.min(overlap((item.tags || []).map(normalize), (current.tags || []).map(normalize)), 5) * 2;
    if (item.source === 'shopify') value += 22;
    if (sameType || shared) value += Math.max(0, 8 - Math.abs(Math.log((item.price + 1) / (current.price + 1))) * 3);
    let personal = 0;
    history.filter(previous => String(previous.id) !== String(current.id)).slice(0, 8).forEach((previous, index) => {
      const affinity = (type && type === normalize(previous.type) ? 12 : 0) + Math.min(overlap(categories(item), categories(previous)), 2) * 7 + (vendor && vendor === normalize(previous.vendor) ? 3 : 0);
      personal += affinity * engagement(previous) * Math.pow(.8, index);
    });
    return value + Math.min(personal, 45);
  }
  function rank(items, current, history) {
    const seen = new Set([String(current.id)]);
    return items.filter(item => {
      if (!item.id || seen.has(String(item.id)) || item.available === false) return false;
      seen.add(String(item.id));
      return true;
    }).map(item => ({ item, value: score(item, current, history) }))
      .sort((a, b) => b.value - a.value || String(a.item.id).localeCompare(String(b.item.id)))
      .map(entry => entry.item);
  }
  function select(items, { desktop, sidebarCount = 4, limit = 8, columns = 4 }) {
    const sidebar = desktop ? items.slice(0, sidebarCount) : [];
    const pool = desktop && items.length >= sidebar.length + columns ? items.slice(sidebar.length) : items;
    let count = Math.min(limit, pool.length);
    if (count >= columns) count -= count % columns;
    return { sidebar, gallery: pool.slice(0, count) };
  }
  globalThis.RumiRecommendationRanking = { score, rank, select, engagement };
  if (typeof document === 'undefined') return;

  const roots = [...document.querySelectorAll('rumi-recommendations')];
  if (!roots.length) return;
  const key = 'rumi-recent-product-interest-v1';
  let current;
  try { current = JSON.parse(roots[0].querySelector('[data-rumi-context]').textContent); } catch (_) { return; }
  let history = [];
  try {
    const saved = JSON.parse(sessionStorage.getItem(key));
    if (Array.isArray(saved)) history = saved.filter(item => item && item.id && Date.now() - item.at < 14400000).slice(0, 24);
  } catch (_) {}
  const previous = history.find(item => String(item.id) === String(current.id));
  const active = { ...current, views: (Number(previous?.views) || 0) + 1, seconds: Number(previous?.seconds) || 0, at: Date.now() };
  history = [active, ...history.filter(item => String(item.id) !== String(current.id))].slice(0, 24);
  const save = () => { try { sessionStorage.setItem(key, JSON.stringify(history)); } catch (_) {} };
  save();

  // Count visible, recently active reading time; never rearrange cards mid-visit.
  let lastInteraction = Date.now();
  let lastTick = Date.now();
  ['pointerdown', 'keydown', 'scroll'].forEach(name => window.addEventListener(name, () => { lastInteraction = Date.now(); }, { passive: true }));
  const recordTime = () => {
    const now = Date.now();
    if (!document.hidden && now - lastInteraction < 60000) active.seconds = Math.min(180, active.seconds + Math.min(15, (now - lastTick) / 1000));
    lastTick = now;
    active.at = now;
    save();
  };
  let dwellTimer = setInterval(recordTime, 15000);
  document.addEventListener('visibilitychange', () => { lastTick = Date.now(); if (!document.hidden) lastInteraction = Date.now(); });
  window.addEventListener('pagehide', () => { recordTime(); clearInterval(dwellTimer); });
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    try {
      const saved = JSON.parse(sessionStorage.getItem(key));
      if (Array.isArray(saved)) history = saved.filter(item => item && item.id && Date.now() - item.at < 14400000).slice(0, 24);
    } catch (_) {}
    const latest = history.find(item => String(item.id) === String(current.id));
    active.views = Math.max(active.views, Number(latest?.views) || 0) + 1;
    active.seconds = Math.max(active.seconds, Number(latest?.seconds) || 0);
    history = [active, ...history.filter(item => String(item.id) !== String(current.id))].slice(0, 24);
    lastTick = lastInteraction = Date.now();
    clearInterval(dwellTimer);
    dwellTimer = setInterval(recordTime, 15000);
    save();
    render();
  });

  const candidates = new Map();
  function readCandidates(template) {
    if (!template) return;
    template.content.querySelectorAll('[data-rumi-candidate]').forEach(node => {
      const data = node.dataset;
      let collections = [], tags = [];
      try { collections = JSON.parse(data.collections); tags = JSON.parse(data.tags); } catch (_) {}
      const candidate = { id: data.id, title: data.title, type: data.type, vendor: data.vendor, price: Number(data.price) || 0, collections: Array.isArray(collections) ? collections : [], tags: Array.isArray(tags) ? tags : [], source: data.source, node };
      const existing = candidates.get(candidate.id);
      if (!existing || candidate.source === 'shopify') candidates.set(candidate.id, candidate);
    });
  }
  roots.forEach(root => { root.setAttribute('aria-busy', 'true'); readCandidates(root.querySelector('[data-rumi-candidate-pool]')); });

  const desktopQuery = window.matchMedia('(min-width:1100px)');
  const mobileQuery = window.matchMedia('(max-width:749px)');
  let galleryLimit = Number(roots.find(root => root.dataset.layout === 'gallery')?.dataset.limit) || 8;
  function render() {
    const ordered = rank([...candidates.values()], current, history);
    const sidebarCount = Number(roots.find(root => root.dataset.layout === 'sidebar')?.dataset.limit) || 4;
    const columns = mobileQuery.matches ? 2 : 4;
    const chosen = select(ordered, { desktop: desktopQuery.matches, sidebarCount, limit: galleryLimit, columns });
    roots.forEach(root => {
      const list = root.querySelector('[data-rumi-recommendation-list]');
      const items = root.dataset.layout === 'sidebar' ? chosen.sidebar : chosen.gallery;
      const existing = [...list.children];
      const samePrefix = existing.length <= items.length && existing.every((node, index) => node.dataset?.id === String(items[index].id));
      if (samePrefix) list.append(...items.slice(existing.length).map(item => item.node.cloneNode(true)));
      else list.replaceChildren(...items.map(item => item.node.cloneNode(true)));
      root.dataset.ready = 'true';
      root.removeAttribute('aria-busy');
      root.closest('.rumi-recommendations').hidden = !items.length;
      if (root.dataset.layout === 'gallery') root.style.setProperty('--recommendation-columns', Math.min(columns, Math.max(1, items.length)));
      if (root.dataset.layout === 'gallery') {
        const button = root.querySelector('[data-rumi-load-more]');
        const count = root.querySelector('[data-rumi-recommendation-count]');
        const available = select(ordered, { desktop: desktopQuery.matches, sidebarCount, limit: ordered.length, columns }).gallery.length;
        if (button) button.hidden = items.length >= available;
        if (count) count.textContent = items.length >= available ? 'All recommendations loaded.' : 'Recommendations loaded.';
      }
    });
  }
  roots.forEach(root => {
    const button = root.querySelector('[data-rumi-load-more]');
    button?.addEventListener('click', () => {
      const list = root.querySelector('[data-rumi-recommendation-list]');
      const previousCount = list.children.length;
      galleryLimit += 8;
      render();
      if (button.hidden) list.children[previousCount]?.querySelector('a')?.focus();
    });
  });
  async function initialize() {
    // One shared Shopify request enriches both placements, with collection fallbacks.
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 1800);
    try {
      const root = roots[0];
      const url = new URL(root.dataset.url, window.location.origin);
      url.searchParams.set('limit', '10');
      url.searchParams.set('intent', 'related');
      url.searchParams.set('product_id', current.id);
      url.searchParams.set('section_id', root.dataset.sectionId);
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error('Recommendations unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      readCandidates(html.querySelector('[data-rumi-candidate-pool]'));
    } catch (_) {
      // The server-rendered collection pool remains usable when the API fails.
    } finally { clearTimeout(timeout); render(); }
  }
  desktopQuery.addEventListener('change', render);
  mobileQuery.addEventListener('change', render);
  initialize();
})();
